import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { ERROR_I18N_KEYS } from '@/lib/dify/errorUtils';

interface ErrorCardProps {
    title?: string;
    message?: string;
    variant?: 'error' | 'warning';
}

/**
 * ErrorCard component for displaying friendly error messages
 */
export const ErrorCard: React.FC<ErrorCardProps> = ({ title, message, variant = 'error' }) => {
    const { t } = useTranslation();

    const displayTitle = title || t(ERROR_I18N_KEYS.RATE_LIMIT.TITLE);
    const displayMessage = message || t(ERROR_I18N_KEYS.RATE_LIMIT.MESSAGE);

    const isWarning = variant === 'warning';

    return (
        <div className={`my-2 rounded-lg border px-4 py-3 ${isWarning
            ? 'border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20'
            : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
        }`}>
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    {isWarning
                        ? <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        : <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    }
                </div>
                <div className="ml-3 flex-1">
                    <h3 className={`text-sm font-medium ${isWarning
                        ? 'text-yellow-800 dark:text-yellow-300'
                        : 'text-red-800 dark:text-red-300'
                    }`}>
                        {displayTitle}
                    </h3>
                    <div className={`mt-1 text-sm ${isWarning
                        ? 'text-yellow-700 dark:text-yellow-400'
                        : 'text-red-700 dark:text-red-400'
                    }`}>
                        {displayMessage}
                    </div>
                </div>
            </div>
        </div>
    );
};
